import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import Slider from 'react-slick'
import type { Settings } from 'react-slick'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { clipboardCopy, toast } from '../../utils'

import { getDepositAccount, getUnmaskedDepositAccount } from '../../api/depositAccount'
import { getWithdrawalAccount } from '../../api/withdrawalAccount'

import { StoresContext } from '../../provider/StoreProvider'

import { useUserInfo } from '../../hooks/useUserInfo'
import { useNavigate } from '../../hooks/useNavigate'

import { Head } from '../../components/head/Head'
import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, CenterWrap, ColumnItem, RowBetween } from '../../components/common'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { StyledText, Text } from '../../components/text'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import type { BankName } from '../../components/bankIcon/BankIcon'

import { Color } from '../../theme/color'

import { ReactComponent as WarningIcon } from '../../assets/gui/warning.svg'

import 'slick-carousel/slick/slick.css'

const HEAD_INFO = {
  title: '예치금 충전 계좌 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '예치금 충전 계좌 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

const SETTINGS: Settings = {
  arrows: false,
  centerMode: true,
  centerPadding: '24px',
  initialSlide: 0,
  infinite: false,
}

export function DepositAccount() {
  const sliderRef = useRef<Slider>(null)

  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)

  const [currentSlide, setCurrentSlide] = useState<number>(0)

  const { isLogin, isCheckingLogin } = useUserInfo()
  const navigate = useNavigate()

  const depositAccount = useQuery({ queryKey: [QUERY_KEY.depositAccount], queryFn: getDepositAccount })
  const unmaskedDepositAccount = useQuery({
    queryKey: [QUERY_KEY.depositAccount, QUERY_KEY.unmasked],
    queryFn: getUnmaskedDepositAccount,
  })
  const withdrawalAccount = useQuery({ queryKey: [QUERY_KEY.withdrawalAccount], queryFn: getWithdrawalAccount })

  useEffect(() => {
    setShowTab(false)
  }, [])

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (depositAccount.isError && depositAccount.error) {
      if (isAxiosError(depositAccount.error)) {
        toast(depositAccount.error.response?.data.message, depositAccount.error)
      }
    }
  }, [depositAccount.isError, depositAccount.error])

  useEffect(() => {
    if (unmaskedDepositAccount.isError && unmaskedDepositAccount.error) {
      if (isAxiosError(unmaskedDepositAccount.error)) {
        toast(unmaskedDepositAccount.error.response?.data.message, unmaskedDepositAccount.error)
      }
    }
  }, [unmaskedDepositAccount.isError, unmaskedDepositAccount.error])

  if (
    depositAccount.isPending ||
    depositAccount.isError ||
    unmaskedDepositAccount.isPending ||
    unmaskedDepositAccount.isError ||
    withdrawalAccount.isPending ||
    withdrawalAccount.isError
  ) {
    return (
      <>
        <Head {...HEAD_INFO} />
        <Wrap>
          <Header type="SUB" title="충전하기" />
          <CenterWrap>
            <Spinner />
          </CenterWrap>
        </Wrap>
      </>
    )
  }

  const copyAccount = () => {
    clipboardCopy(unmaskedDepositAccount.data.account, {
      message: '✓ 예치금 계좌 번호 복사가 완료되었어요',
      isFooter: true,
    })
    sliderRef.current?.slickGoTo(1)
  }

  const successRemit = () => navigate(-1)

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="충전하기" />
        <StyledContent backgroundColor="gray_1">
          <Title size={24} weight={600} lineHeight={34}>
            예치금 충전 방법을 안내해드려요
          </Title>
          <SlideContainer $gap={40} $currentSlide={currentSlide}>
            <Slider {...SETTINGS} ref={sliderRef} beforeChange={(_, next) => setCurrentSlide(next)}>
              <SlideCard>
                <SlideDesc>
                  <SlideIndex>1</SlideIndex>
                  <Text size={16} weight={500} lineHeight={24} $wordBreak="keep-all">
                    {depositAccount.data.bankOwner}님 전용 아티피오 농협 예치금 계좌번호를 복사해주세요.
                  </Text>
                </SlideDesc>
                <DepositAccountBox $gap={24}>
                  <RowBetween>
                    <AlignCenterItem $gap={16}>
                      <BankIcon size={46} name="NH농협은행" />
                      <Text size={18} weight={600}>
                        NH농협은행
                      </Text>
                    </AlignCenterItem>
                    <Button type="secondary" size="compact" onClick={copyAccount}>
                      복사
                    </Button>
                  </RowBetween>
                  <RowBetween>
                    <Text size={14} weight={600} color="text_secondary">
                      계좌번호
                    </Text>
                    <Text size={14} color="text_secondary">
                      {depositAccount.data.account}
                    </Text>
                  </RowBetween>
                </DepositAccountBox>
              </SlideCard>

              <SlideCard>
                <SlideDesc>
                  <SlideIndex>2</SlideIndex>
                  <Text size={16} weight={500} lineHeight={24} $wordBreak="keep-all">
                    {withdrawalAccount.data.bankName}의 앱 또는 웹에서 충전할 예치금을 송금해주세요.
                  </Text>
                </SlideDesc>
                <ColumnItem $gap={24}>
                  <AlignCenterItem $gap={16}>
                    <BankIcon size={46} name={withdrawalAccount.data.bankName as BankName} />
                    <Text size={18} weight={600}>
                      {withdrawalAccount.data.bankName}
                    </Text>
                  </AlignCenterItem>
                  <ColumnItem $gap={16}>
                    <RowBetween>
                      <Text size={14} weight={600} color="text_secondary">
                        계좌번호
                      </Text>
                      <Text size={14} color="text_secondary">
                        {withdrawalAccount.data.account}
                      </Text>
                    </RowBetween>
                    <RowBetween>
                      <Text size={14} weight={600} color="text_secondary">
                        예금주
                      </Text>
                      <Text size={14} color="text_secondary">
                        {withdrawalAccount.data.bankOwner}
                      </Text>
                    </RowBetween>
                  </ColumnItem>
                </ColumnItem>
                <WarningText size={12} weight={500} color="error">
                  <WarningIcon />
                  {withdrawalAccount.data.bankName} 외 타 은행이나 증권사에서는 송금이 불가능해요.
                </WarningText>
              </SlideCard>
            </Slider>
            <SlideIndexBarContainer>
              <SlideIndexBar $currentSlide={currentSlide} />
            </SlideIndexBarContainer>
          </SlideContainer>
          <NoticeContainerBox $isRemit={currentSlide === 1}>
            <NoticeContainer
              title="안내사항"
              list={[
                '최소 충전 가능 금액은 10,000원이에요.',
                '예치금 충전은 은행 정산 상태에 따라 최대 10분 정도 소요될 수 있어요.',
                '은행점검시간(23:45 ~ 00:15)에는 충전 및 출금 신청이 제한 될 수 있어요.',
                '토스/카카오뱅크 내 오픈 뱅킹 연동 계좌나 간편 결제(네이버페이, 카카오페이 등)를 통한 입금은 금융사 정책으로 인해 제한돼요.',
              ]}
              $backgroundColor="gray_1"
            />
          </NoticeContainerBox>
        </StyledContent>
        <Footer>
          {currentSlide === 0 ? (
            <Button type="primary" size="large" block={true} onClick={copyAccount}>
              농협 예치금 계좌번호 복사
            </Button>
          ) : (
            <Button type="primary" size="large" block={true} onClick={successRemit}>
              송금 완료
            </Button>
          )}
        </Footer>
      </Wrap>
    </>
  )
}

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`

const Title = styled(Text)`
  margin: 40px 24px 24px;
`

const SlideContainer = styled(ColumnItem)<{ $currentSlide: number }>`
  position: relative;
  .slick-list {
    height: 100%;
  }
  .slick-track {
    display: flex;
    gap: 12px;
    height: 100%;
    transition: 0.3s;
    &::before,
    &::after {
      display: none;
    }
    .slick-slide {
      height: 100% !important;
      ${({ $currentSlide }) => ($currentSlide === 0 ? 'width: calc(100% / 2 - 15px) !important;' : '')}
      transition: 0.3s;
      > div {
        height: 100%;
      }
    }
    ${({ $currentSlide }) =>
      $currentSlide === 1
        ? `
            .slick-slide:first-child {
              width: calc(100% / 2 + 15px) !important;
            }
            `
        : ''}
  }
`

const SlideCard = styled.div`
  display: flex !important;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  padding: 24px;
  border-radius: 24px;
  background-color: ${Color.white};
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.05);
`

const SlideDesc = styled.div`
  display: flex;
  gap: 16px;
`

const SlideIndex = styled.span`
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${Color.primary_5};
  font-size: 16px;
  font-weight: 700;
  color: ${Color.text_white};
`

const DepositAccountBox = styled(ColumnItem)`
  margin-top: 24px;
`

const WarningText = styled(StyledText)`
  display: flex;
  align-items: center;
  gap: 4px;
`

const SlideIndexBarContainer = styled.div`
  display: flex;
  overflow: hidden;
  position: relative;
  width: 50px;
  height: 8px;
  margin: 0 auto 32px;
  border-radius: 50vh;
  background-color: #d9d9d9;
`

const SlideIndexBar = styled.div<{ $currentSlide: number }>`
  position: absolute;
  ${({ $currentSlide }) => `left: ${$currentSlide === 0 ? '0' : '50%'};`}
  width: 50%;
  height: 100%;
  border-radius: 50vh;
  background-color: ${Color.primary_5};
  transition: 0.3s;
`

const NoticeContainerBox = styled.div<{ $isRemit: boolean }>`
  opacity: ${({ $isRemit }) => ($isRemit ? 1 : 0)};
  overflow: hidden;
  height: ${({ $isRemit }) => ($isRemit ? 'auto' : '0')};
  transition: 0.3s;
`
